import React, { useEffect, useMemo, useState } from "react";
import {
  Warning,
  Plus,
  TrashSimple,
  PencilSimple,
  X,
  FileText,
  CurrencyEur,
  Buildings,
  User as UserIcon,
  Clock,
  UploadSimple,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import {
  fetchFines,
  createFine,
  updateFine,
  deleteFine,
  toggleFinePaid,
  fetchFinesStats,
  fetchTrucks,
  uploadPhoto,
  photoUrl,
} from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

const PAID_BY_LABEL = {
  driver: "Chauffeur",
  company: "Société",
};
const PAID_BY_COLOR = {
  driver: "bg-emerald-600 text-white",
  company: "bg-blue-600 text-white",
};

export default function AmendesPage() {
  const [fines, setFines] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [stats, setStats] = useState({ total_count: 0, total_amount: 0, paid_amount: 0, paid_by_driver: 0, paid_by_company: 0, unpaid: 0, unpaid_count: 0, drivers: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [filterStatus, setFilterStatus] = useState("all"); // all | unpaid | paid

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [f, t, s] = await Promise.all([fetchFines(), fetchTrucks(), fetchFinesStats()]);
      setFines(f);
      setTrucks(t);
      setStats(s);
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const truckById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);

  const filtered = useMemo(() => {
    if (filterStatus === "all") return fines;
    if (filterStatus === "unpaid") return fines.filter((f) => !f.is_paid);
    if (filterStatus === "paid") return fines.filter((f) => f.is_paid);
    return fines;
  }, [fines, filterStatus]);

  const handleTogglePaid = async (fine, e) => {
    e.stopPropagation();
    try {
      await toggleFinePaid(fine.id);
      toast.success(fine.is_paid ? "Marquée non payée" : "Marquée comme payée");
      load({ silent: true });
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500">NY Trans</div>
        <h1 className="text-3xl font-bold serif inline-flex items-center gap-2">
          <Warning size={26} weight="fill" className="text-amber-600" /> Amendes
        </h1>
      </div>

      {/* Alert banner for unpaid fines */}
      {stats.unpaid_count > 0 && (
        <div
          className="mb-4 border-2 border-red-600 bg-red-50 px-4 py-3 flex items-start gap-3"
          data-testid="fines-unpaid-alert"
        >
          <Warning size={22} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-bold text-red-900">
              Rappel · Amendes non payées
            </div>
            <div className="text-sm text-red-800 mt-0.5">
              <strong>{stats.unpaid_count}</strong> amende(s) impayée(s) pour un total de{" "}
              <strong>{stats.unpaid.toFixed(2)} €</strong>. Marque-les comme payées après règlement pour clôturer le suivi.
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <SummaryCard
          label="Total"
          value={`${stats.total_amount.toFixed(2)} €`}
          sub={`${stats.total_count} amende${stats.total_count > 1 ? "s" : ""}`}
          icon={<CurrencyEur size={18} weight="bold" />}
          cls="border-zinc-900"
        />
        <SummaryCard
          label="Payées"
          value={`${stats.paid_amount.toFixed(2)} €`}
          sub="dont chauffeur / société"
          icon={<CheckIcon />}
          cls="border-emerald-500"
        />
        <SummaryCard
          label="À la charge chauffeur"
          value={`${stats.paid_by_driver.toFixed(2)} €`}
          icon={<UserIcon size={18} weight="bold" />}
          cls="border-emerald-300"
        />
        <SummaryCard
          label="Non payées"
          value={`${stats.unpaid.toFixed(2)} €`}
          sub={`${stats.unpaid_count} amende${stats.unpaid_count > 1 ? "s" : ""}`}
          icon={<Clock size={18} weight="bold" />}
          cls={stats.unpaid_count > 0 ? "border-red-500" : "border-zinc-300"}
        />
      </div>

      {/* Filters + Action */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex gap-1">
          {[
            { k: "all", label: "Toutes" },
            { k: "unpaid", label: `Non payées${stats.unpaid_count > 0 ? ` (${stats.unpaid_count})` : ""}` },
            { k: "paid", label: "Payées" },
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => setFilterStatus(k)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${
                filterStatus === k
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`fines-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setDialog({ open: true, editing: null })}
          className="ml-auto px-3 py-2 text-xs font-bold uppercase tracking-widest border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-2"
          data-testid="fines-new"
        >
          <Plus size={14} weight="bold" /> Nouvelle amende
        </button>
      </div>

      {loading && <div className="text-center py-10 text-zinc-500">Chargement…</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-300">
          <Warning size={32} weight="duotone" className="mx-auto mb-2" />
          <div className="text-sm font-semibold">
            {fines.length === 0 ? "Aucune amende enregistrée" : "Aucune amende pour ce filtre"}
          </div>
        </div>
      )}

      {/* Fines list */}
      <div className="space-y-1.5">
        {filtered.map((f) => {
          const t = f.truck_id ? truckById[f.truck_id] : null;
          const paidBy = f.paid_by || "driver";
          const isPaid = !!f.is_paid;
          return (
            <div
              key={f.id}
              onClick={() => setDialog({ open: true, editing: f })}
              className={`border p-3 cursor-pointer hover:bg-zinc-50 ${
                !isPaid ? "bg-red-50 border-red-400" : "bg-white border-zinc-200"
              }`}
              data-testid={`fine-row-${f.id}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={(e) => handleTogglePaid(f, e)}
                  className={`shrink-0 w-6 h-6 border-2 flex items-center justify-center ${
                    isPaid
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-red-500 bg-white hover:bg-red-100"
                  }`}
                  title={isPaid ? "Marquer comme non payée" : "Marquer comme payée"}
                  data-testid={`fine-toggle-paid-${f.id}`}
                >
                  {isPaid && <CheckCircle size={14} weight="bold" />}
                </button>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-base">
                      {f.reason || "— Motif à préciser —"}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${PAID_BY_COLOR[paidBy]}`}
                      title="À la charge de"
                    >
                      {PAID_BY_LABEL[paidBy]}
                    </span>
                    {!isPaid ? (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white inline-flex items-center gap-1">
                        <Warning size={10} weight="fill" /> Non payée
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white inline-flex items-center gap-1">
                        <CheckCircle size={10} weight="fill" /> Payée
                      </span>
                    )}
                    {f.doc && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white inline-flex items-center gap-1">
                        <FileText size={10} weight="bold" /> PJ
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    {f.fine_date}
                    {f.driver_name && (
                      <span className="ml-2">
                        · <span className="font-bold">{f.driver_name}</span>
                      </span>
                    )}
                    {t && (
                      <span className="ml-2">
                        · <span className="mono uppercase font-bold">{t.license_plate}</span>{" "}
                        <span className="text-zinc-400">{t.brand}</span>
                      </span>
                    )}
                    {isPaid && f.paid_at && (
                      <span className="ml-2 text-emerald-700">
                        · Payée le {f.paid_at.slice(0, 10)}
                      </span>
                    )}
                  </div>
                  {f.notes && (
                    <div className="text-[11px] italic text-zinc-500 mt-1 line-clamp-2">
                      {f.notes}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold mono">
                    {f.amount_eur.toFixed(2)} €
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Driver ranking */}
      {stats.drivers.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">
            Classement des chauffeurs (triés par montant impayé)
          </div>
          <div className="space-y-1">
            {stats.drivers.slice(0, 10).map((d) => (
              <div
                key={d.name}
                className={`flex items-center justify-between px-3 py-2 border ${
                  d.unpaid > 0 ? "bg-red-50 border-red-300" : "bg-white border-zinc-200"
                }`}
                data-testid={`fines-driver-${d.name}`}
              >
                <div>
                  <div className="font-bold">{d.name}</div>
                  <div className="text-[10px] text-zinc-500 mono">
                    {d.count} amende{d.count > 1 ? "s" : ""}
                    {d.unpaid_count > 0 && (
                      <span className="ml-2 text-red-600 font-bold">
                        · {d.unpaid_count} impayée{d.unpaid_count > 1 ? "s" : ""} ({d.unpaid.toFixed(2)} €)
                      </span>
                    )}
                  </div>
                </div>
                <div className="mono font-bold">{d.total.toFixed(2)} €</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {dialog.open && (
        <FineDialog
          open={dialog.open}
          editing={dialog.editing}
          trucks={trucks}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => {
            setDialog({ open: false, editing: null });
            load();
          }}
        />
      )}
    </div>
  );
}

function CheckIcon() {
  return <CheckCircle size={18} weight="fill" className="text-emerald-600" />;
}

function SummaryCard({ label, value, sub, icon, cls }) {
  return (
    <div className={`bg-white border-2 p-3 ${cls}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-600">
        {icon} {label}
      </div>
      <div className="mt-1 text-xl font-bold mono">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
    </div>
  );
}

/* ===================== DIALOG ===================== */

function FineDialog({ open, editing, trucks, onClose, onSaved }) {
  const [form, setForm] = useState({
    truck_id: editing?.truck_id || "",
    driver_name: editing?.driver_name || "",
    fine_date: editing?.fine_date || new Date().toISOString().slice(0, 10),
    amount_eur: editing?.amount_eur ?? "",
    reason: editing?.reason || "",
    paid_by: editing?.paid_by || "driver",
    is_paid: editing?.is_paid || false,
    doc: editing?.doc || null,
    notes: editing?.notes || "",
  });
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Fichier trop lourd (max 8 MB)");
    setUploading(true);
    try {
      const { path } = await uploadPhoto(f);
      setForm((p) => ({ ...p, doc: path }));
      toast.success("Pièce jointe enregistrée");
    } catch {
      toast.error("Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.fine_date) return toast.error("Date requise");
    if (!form.amount_eur || parseFloat(form.amount_eur) <= 0) return toast.error("Montant requis");
    const body = {
      truck_id: form.truck_id || null,
      driver_name: form.driver_name || "",
      fine_date: form.fine_date,
      amount_eur: parseFloat(form.amount_eur),
      reason: form.reason,
      paid_by: form.paid_by,
      is_paid: !!form.is_paid,
      doc: form.doc,
      notes: form.notes,
    };
    try {
      if (editing) await updateFine(editing.id, body);
      else await createFine(body);
      toast.success(editing ? "Amende mise à jour" : "Amende enregistrée");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Supprimer cette amende ?")) return;
    try {
      await deleteFine(editing.id);
      toast.success("Amende supprimée");
      onSaved();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-lg">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-lg font-bold">
            <Warning size={20} weight="fill" className="text-amber-600" />
            {editing ? "Modifier l'amende" : "Nouvelle amende"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Date *</Label>
              <Input
                type="date"
                value={form.fine_date}
                onChange={(e) => setForm({ ...form, fine_date: e.target.value })}
                className="rounded-none border-zinc-900"
                data-testid="fine-date"
              />
            </div>
            <div>
              <Label className="label-tag">Montant (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount_eur}
                onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                placeholder="ex. 135.00"
                data-testid="fine-amount"
              />
            </div>
          </div>

          <div>
            <Label className="label-tag">Motif</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="rounded-none border-zinc-900"
              placeholder="Excès de vitesse, stationnement, feu rouge…"
              data-testid="fine-reason"
            />
          </div>

          <div>
            <Label className="label-tag">Camion (facultatif)</Label>
            <TruckPicker
              value={form.truck_id}
              trucks={trucks}
              onChange={(v) => setForm({ ...form, truck_id: v })}
            />
          </div>

          <div>
            <Label className="label-tag">Chauffeur concerné</Label>
            <Input
              value={form.driver_name}
              onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
              className="rounded-none border-zinc-900"
              placeholder="Nom du chauffeur au moment de l'infraction"
              data-testid="fine-driver"
            />
          </div>

          <div>
            <Label className="label-tag">À la charge de</Label>
            <Select
              value={form.paid_by}
              onValueChange={(v) => setForm({ ...form, paid_by: v })}
            >
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="fine-paidby">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="driver">Chauffeur</SelectItem>
                <SelectItem value="company">Société</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <label
            className="flex items-center gap-2 px-3 py-2 border-2 border-zinc-300 bg-white cursor-pointer hover:border-zinc-900"
            data-testid="fine-paid-toggle"
          >
            <input
              type="checkbox"
              checked={!!form.is_paid}
              onChange={(e) => setForm({ ...form, is_paid: e.target.checked })}
              className="w-4 h-4"
            />
            <CheckCircle
              size={16}
              weight="bold"
              className={form.is_paid ? "text-emerald-600" : "text-zinc-400"}
            />
            <span className="text-xs uppercase tracking-widest font-bold">
              {form.is_paid ? "Amende payée" : "Amende non encore payée"}
            </span>
          </label>

          <div>
            <Label className="label-tag">Pièce jointe (photo ou PDF)</Label>
            <label
              className="block bg-zinc-900 text-white text-center py-2 px-3 text-xs uppercase tracking-widest font-bold cursor-pointer hover:bg-zinc-700"
              data-testid="fine-doc-label"
            >
              <UploadSimple size={12} weight="bold" className="inline mr-1" />
              {uploading ? "Upload…" : form.doc ? "Remplacer la pièce" : "Téléverser l'amende"}
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={onFile}
                className="hidden"
                data-testid="fine-doc-input"
              />
            </label>
            {form.doc && (
              <div className="mt-2 border border-zinc-300 p-2 inline-flex items-center gap-2">
                <FileText size={16} weight="bold" />
                <a
                  href={photoUrl(form.doc)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-bold uppercase tracking-widest underline"
                >
                  Voir la pièce
                </a>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, doc: null })}
                  className="text-xs font-bold uppercase text-red-600"
                >
                  Retirer
                </button>
              </div>
            )}
          </div>

          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-none border-zinc-900"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing && (
            <button
              onClick={remove}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 mr-auto"
              data-testid="fine-delete"
            >
              <TrashSimple size={14} weight="bold" /> Supprimer
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2"
          >
            <X size={14} weight="bold" /> Annuler
          </button>
          <button
            onClick={save}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2"
            data-testid="fine-save"
          >
            <PencilSimple size={14} weight="bold" /> {editing ? "Mettre à jour" : "Enregistrer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TruckPicker({ value, trucks, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => trucks.find((t) => t.id === value), [trucks, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 border border-zinc-900 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-700 text-left"
          data-testid="fine-truck-picker"
        >
          <span className={`truncate ${selected ? "" : "text-zinc-400"}`}>
            {selected
              ? `${selected.license_plate} · ${(selected.brand || "").toUpperCase()}`
              : "Aucun camion sélectionné"}
          </span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Plaque, marque, modèle…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucun camion.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="rounded-none text-sm italic text-zinc-500"
              >
                — Aucun camion —
              </CommandItem>
              {trucks.map((t) => {
                const searchValue = `${t.license_plate} ${t.brand || ""} ${t.model || ""} ${t.site || ""}`.toLowerCase();
                const isSelected = t.id === value;
                return (
                  <CommandItem
                    key={t.id}
                    value={searchValue}
                    onSelect={() => {
                      onChange(t.id);
                      setOpen(false);
                    }}
                    className="rounded-none text-sm flex items-center gap-2"
                    data-testid={`fine-truck-option-${t.id}`}
                  >
                    <Check
                      size={12}
                      weight="bold"
                      className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
                    />
                    <span className="font-bold mono uppercase">{t.license_plate}</span>
                    <span className="text-xs text-zinc-500 truncate">
                      {(t.brand || "").toUpperCase()} · {(t.site || "").toUpperCase()}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
